import { useState, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Lightbulb, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { AltchaWidget } from '@/components/AltchaWidget';

const featureRequestSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(100, 'Title must be less than 100 characters'),
  description: z.string().trim().min(10, 'Please provide more detail (at least 10 characters)').max(1000, 'Description must be less than 1000 characters'),
});

interface FeatureRequestDialogProps {
  userEmail: string;
  trigger?: ReactNode | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function FeatureRequestDialog({ userEmail, trigger, open: controlledOpen, onOpenChange }: FeatureRequestDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (nextOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ title?: string; description?: string }>({});
  const [altchaToken, setAltchaToken] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = featureRequestSchema.safeParse({ title, description });
    if (!result.success) {
      const fieldErrors: { title?: string; description?: string } = {};
      result.error.errors.forEach((err) => {
        if (err.path[0] === 'title') fieldErrors.title = err.message;
        if (err.path[0] === 'description') fieldErrors.description = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      if (!altchaToken) {
        toast.error('Please complete the verification.');
        setIsSubmitting(false);
        return;
      }
      const { data, error } = await supabase.functions.invoke('submit-feature-request', {
        body: {
          title: title.trim(),
          description: description.trim(),
          userEmail,
          altchaToken,
        },
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to submit feature request');
      }

      toast.success('Feature request submitted successfully!');
      setTitle('');
      setDescription('');
      setAltchaToken(null);
      setOpen(false);
    } catch (err) {
      console.error('Feature request error:', err);
      toast.error('Failed to submit feature request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      document.documentElement.style.pointerEvents = '';
      document.body.style.pointerEvents = '';
      setTitle('');
      setDescription('');
      setErrors({});
      setAltchaToken(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger === null ? null : (
        <DialogTrigger asChild>
          {trigger ? (
            trigger
          ) : (
            <Button variant="outline" size="sm">
              <Lightbulb className="h-4 w-4 mr-2" />
              Request Feature
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="cyber-card max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-xl font-mono">
            Request a Feature
          </DialogTitle>
          <DialogDescription>
            Have an idea to improve ShadowScan? We'd love to hear it!
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="feature-title">Feature Title</Label>
            <Input
              id="feature-title"
              placeholder="Brief title for your idea"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-muted/50"
              maxLength={100}
            />
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="feature-description">Description</Label>
            <Textarea
              id="feature-description"
              placeholder="Describe the feature you'd like to see. What problem would it solve? How would it help you?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-muted/50 min-h-[120px] resize-none"
              maxLength={1000}
            />
            <div className="flex justify-between items-center">
              {errors.description ? (
                <p className="text-sm text-destructive">{errors.description}</p>
              ) : (
                <span />
              )}
              <span className="text-xs text-muted-foreground">
                {description.length}/1000
              </span>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <div className="rounded-md border border-border/50 p-3 bg-muted/30 mb-3">
              <p className="text-xs text-muted-foreground mb-2">Please verify to submit</p>
              <AltchaWidget onToken={setAltchaToken} />
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
